import React, { useState } from 'react';
import { Mail, MessageCircle, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * NewsletterSignup — public email + optional WhatsApp opt-in.
 *
 * Why it's in the footer: top real-estate platforms (Zillow, Property24,
 * PropertyGuru) use the footer as their primary list-growth surface. An
 * owned email channel survives social-algorithm changes.
 *
 * Posts to /api/newsletter/subscribe (rate-limited 10/15 min per IP).
 * State machine: idle → submitting → success (or error).
 */
const NewsletterSignup = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [wantsWhatsapp, setWantsWhatsapp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !email.includes('@')) {
      setError('Adresse email invalide');
      return;
    }
    if (wantsWhatsapp && phone.replace(/\D/g, '').length < 8) {
      setError('Numéro WhatsApp requis (ex : +237 6XX XX XX XX)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/newsletter/subscribe`, {
        email: email.trim(),
        language: i18n.language || 'fr',
        source: 'footer',
        whatsapp: wantsWhatsapp,
        phone: wantsWhatsapp ? phone.trim() : '',
      });
      setSuccess(true);
      setEmail('');
      setPhone('');
      setWantsWhatsapp(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError('Trop de tentatives. Patientez quelques minutes.');
      } else {
        setError(err?.response?.data?.detail || 'Erreur — veuillez réessayer.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="newsletter-success"
        className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/40 to-emerald-950/40 p-6 text-center"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
        <p className="mt-2 text-base font-semibold text-white">Bienvenue à bord !</p>
        <p className="mt-1 text-sm text-emerald-200">
          {wantsWhatsapp
            ? 'Vous recevrez nos prochaines annonces par email et WhatsApp.'
            : 'Vous recevrez nos prochaines annonces en avant-première.'}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="newsletter-signup"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-600 via-red-600 to-rose-700 p-6 sm:p-7 mb-12"
    >
      <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-amber-300/20 blur-3xl" />
      <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-orange-300/20 blur-3xl" />
      <div className="relative grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 text-label font-bold uppercase tracking-wider text-white">
            <Mail className="h-3.5 w-3.5" /> Newsletter Realty237
          </div>
          <h3 className="mt-3 text-2xl font-display font-bold leading-tight text-white sm:text-3xl">
            Recevez les nouvelles annonces en avant-première
          </h3>
          <p className="mt-2 text-sm text-white/85">
            Biens premium, opportunités sur saisies bancaires, conseils d'experts —
            directement dans votre boîte. Désinscription en 1 clic.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3" data-testid="newsletter-form">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre.email@example.cm"
              className="flex-1 rounded-lg border-0 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
              data-testid="newsletter-email-input"
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-stone-900 px-5 py-3 text-sm font-semibold text-white shadow-md transition-colors hover:bg-stone-800 disabled:opacity-60"
              data-testid="newsletter-submit-btn"
            >
              {submitting ? '…' : <><Send className="h-4 w-4" /> S'inscrire</>}
            </button>
          </div>

          <label className="flex items-start gap-2 text-xs text-white/90 cursor-pointer" data-testid="newsletter-whatsapp-toggle-label">
            <input
              type="checkbox"
              checked={wantsWhatsapp}
              onChange={(e) => setWantsWhatsapp(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/50 bg-white/20 text-emerald-500 focus:ring-emerald-400"
              data-testid="newsletter-whatsapp-checkbox"
            />
            <span className="flex items-center gap-1">
              <MessageCircle className="h-3.5 w-3.5" />
              Je veux aussi recevoir les annonces par <strong>WhatsApp</strong>
            </span>
          </label>

          {wantsWhatsapp && (
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+237 6XX XX XX XX"
              className="w-full rounded-lg border-0 bg-white px-4 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
              data-testid="newsletter-phone-input"
              disabled={submitting}
            />
          )}

          {error && (
            <p className="text-xs text-yellow-100" role="alert" data-testid="newsletter-error">
              {error}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default NewsletterSignup;
